import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import * as api from '#shared/api/stats';
import type { DeltaSource, RatingCard, RatingDelta } from '#shared/schemas/rating';
import { unratedCard } from '#shared/schemas/rating';
import { api as client } from '@/lib/api';
import { offlineQuery } from '@/lib/query';

/**
 * Stats queries. Every key starts with 'stats', so one invalidation of STATS_KEY refreshes the lot
 * (studio does that after a portrait change; a finished game does it through the staleTime and the
 * refetch on focus). The whole ratings list is one cached request: matchmaking, Draft Night and
 * every card on the app read it, and it is prefetched for offline.
 */

export const STATS_KEY = ['stats'] as const;

export const statsKeys = {
  all: STATS_KEY,
  ratings: ['stats', 'ratings'] as const,
  history: (id: string) => ['stats', 'history', id] as const,
  deltas: (kind: string, id: string) => ['stats', 'deltas', kind, id] as const,
  player: (id: string, window: string) => ['stats', 'player', id, window] as const,
  log: (id: string) => ['stats', 'log', id] as const,
  leaderboard: (stat: string, window: string) => ['stats', 'leaderboard', stat, window] as const,
  records: ['stats', 'records'] as const,
  matrix: ['stats', 'h2h', 'matrix'] as const,
  h2h: (a: string, b: string) => ['stats', 'h2h', a, b] as const,
  rivalries: (playerId: string | null) => ['stats', 'rivalries', playerId ?? 'all'] as const,
  badges: (playerId: string | null) => ['stats', 'badges', playerId ?? 'all'] as const,
  feed: ['stats', 'feed'] as const,
};

export const fetchRatings = () => client.call(api.listRatings, {});

/** Offline prefetch of the ratings list (registered by the module's init). */
export const ratingsQuery = () => offlineQuery(statsKeys.ratings, fetchRatings);

export function useRatingCardsQuery() {
  return useQuery({ ...ratingsQuery(), staleTime: 30_000 });
}

export interface CardsView {
  cards: RatingCard[];
  byId: ReadonlyMap<string, RatingCard>;
  get: (playerId: string) => RatingCard;
  isLoading: boolean;
}

export function useCardsView(): CardsView {
  const q = useRatingCardsQuery();
  return useMemo(() => {
    const cards = q.data ?? [];
    const byId = new Map(cards.map((c) => [c.playerId, c]));
    return { cards, byId, get: (id: string) => byId.get(id) ?? unratedCard(id), isLoading: q.isPending };
  }, [q.data, q.isPending]);
}

/** Deltas of a game or drill session. null until the server has computed them; polls a few times meanwhile. */
export function useDeltasQuery(source: DeltaSource | null): RatingDelta[] | null {
  const q = useQuery({
    queryKey: statsKeys.deltas(source?.kind ?? 'none', source?.id ?? 'none'),
    enabled: source !== null,
    queryFn: () =>
      client.call(api.getDeltas, { query: { kind: (source as DeltaSource).kind, id: (source as DeltaSource).id } }),
    refetchInterval: (query) => (query.state.data === null && query.state.dataUpdateCount < 10 ? 3000 : false),
    staleTime: 30_000,
  });
  return q.data ?? null;
}

export function useHistory(playerId: string) {
  return useQuery({
    queryKey: statsKeys.history(playerId),
    queryFn: () => client.call(api.ratingHistory, { params: { id: playerId } }),
    staleTime: 30_000,
  });
}

export function usePlayerStats(playerId: string, window: 'all' | '30d' = 'all') {
  return useQuery({
    queryKey: statsKeys.player(playerId, window),
    queryFn: () => client.call(api.playerStats, { params: { id: playerId }, query: { window } }),
    staleTime: 30_000,
  });
}

export function useGameLog(playerId: string) {
  return useQuery({
    queryKey: statsKeys.log(playerId),
    queryFn: () => client.call(api.playerGameLog, { params: { id: playerId }, query: { limit: 8 } }),
    staleTime: 30_000,
  });
}

export function useLeaderboard(stat: string, window: 'all' | '30d') {
  return useQuery({
    queryKey: statsKeys.leaderboard(stat, window),
    queryFn: () => client.call(api.leaderboards, { query: { stat, window, limit: 50 } }),
    staleTime: 30_000,
  });
}

export function useRecords() {
  return useQuery({ queryKey: statsKeys.records, queryFn: () => client.call(api.records, {}), staleTime: 30_000 });
}

export function useMatrix() {
  return useQuery({ queryKey: statsKeys.matrix, queryFn: () => client.call(api.h2hMatrix, {}), staleTime: 30_000 });
}

export function useHeadToHead(a: string | null, b: string | null) {
  return useQuery({
    queryKey: statsKeys.h2h(a ?? '', b ?? ''),
    enabled: !!a && !!b && a !== b,
    queryFn: () => client.call(api.headToHead, { query: { a: a as string, b: b as string } }),
    staleTime: 30_000,
  });
}

export function useRivalries(playerId: string | null, limit = 3) {
  return useQuery({
    queryKey: [...statsKeys.rivalries(playerId), limit],
    queryFn: () => client.call(api.rivalries, { query: { ...(playerId ? { playerId } : {}), limit } }),
    staleTime: 30_000,
  });
}

export function useBadges(playerId: string | null) {
  return useQuery({
    queryKey: statsKeys.badges(playerId),
    queryFn: () => client.call(api.badges, { query: playerId ? { playerId } : {} }),
    staleTime: 30_000,
  });
}

/** The first page of the feed (Home). */
export function useFeedPreview(limit = 4) {
  return useQuery({
    queryKey: [...statsKeys.feed, 'preview', limit],
    queryFn: () => client.call(api.feed, { query: { limit } }),
    staleTime: 30_000,
  });
}

/** The whole feed, a page at a time. */
export function useFeedPages() {
  return useInfiniteQuery({
    queryKey: [...statsKeys.feed, 'pages'],
    initialPageParam: undefined as { before: number; beforeId?: string } | undefined,
    queryFn: ({ pageParam }) =>
      client.call(api.feed, {
        query: {
          limit: 20,
          ...(pageParam === undefined
            ? {}
            : { before: pageParam.before, ...(pageParam.beforeId ? { beforeId: pageParam.beforeId } : {}) }),
        },
      }),
    getNextPageParam: (last) =>
      last.nextBefore === null
        ? undefined
        : { before: last.nextBefore, ...(last.nextBeforeId ? { beforeId: last.nextBeforeId } : {}) },
    staleTime: 30_000,
  });
}
